import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Alert, Button, Modal, Tooltip } from "@mantine/core";
import { bindActionCreators } from "redux";
import { updateAppointmentStatus } from "../../data/actions/appointmentActions";
import { AppointmentStatus } from "../../types/appointments/AppointmentStatus";
import {
  IPublishedAppointment,
  IPublishedSupervisorAppointmentDetails,
} from "../../types/appointments/PublishedAppointment";
import { IVenue } from "../../types/venue/Venue";
import { toDateTime } from "../../util/date+helper";
import RejectAppointment from "../appointments/RejectAppointment";
import { ShiftSessionLink } from "../common/Links";

export interface IUpcomingSupervisorRowProps {
  appointment: IPublishedAppointment<IPublishedSupervisorAppointmentDetails>;
  venue: IVenue;
  didUpdateStatus: (AppointmentStatus) => void;
  actions: {
    updateAppointmentStatus: Function;
  };
}

const UnpaddedButton: React.FunctionComponent<any> = ({
  children,
  style,
  ...props
}) => (
  <Button
    style={{ padding: "0px", fontSize: "15px", ...(style || {}) }}
    {...props}
  >
    {children}
  </Button>
);

function UpcomingSupervisorRow(props: IUpcomingSupervisorRowProps) {
  const [isRejectingStatus, beginReject] =
    React.useState<AppointmentStatus | null>(null);
  const [error, updateError] = React.useState("");

  const g = props.appointment;

  // var action: any = <p style={{color: "darkgray"}}>{AppointmentStatus.toString(g.status)}</p>;

  const updateAppointmentStatusWrapped = (status: AppointmentStatus) => () => {
    props.actions
      .updateAppointmentStatus(g._id, status)
      .then((apt) => props.didUpdateStatus(status))
      .catch((er) => updateError(er.message));
  };

  const tags = ["Supervising", `$${Number(g.total).toFixed(2)}`].map((t) => ({
    color: "#467bcf",
    text: t,
  }));

  let affirmitiveText: string = AppointmentStatus.toString(g.status);
  let affirmitiveAction: Function | null = null;
  let rejectStatus: AppointmentStatus | null = AppointmentStatus.rejected;
  let rejectText: string = "Reject";

  if (g.status == AppointmentStatus.published) {
    affirmitiveText = "Confirm";
    affirmitiveAction = updateAppointmentStatusWrapped(
      AppointmentStatus.confirmed,
    );
    rejectStatus = AppointmentStatus.rejected;
  } else if (g.status == AppointmentStatus.confirmed) {
    if (toDateTime(g.finish).minus({ minutes: 5 }) < DateTime.utc()) {
      affirmitiveText = "Complete";
      affirmitiveAction = updateAppointmentStatusWrapped(
        AppointmentStatus.completed,
      );
      rejectStatus = AppointmentStatus.notCompleted;
      rejectText = "Report a problem";
    } else {
      // can't complete a game that's already confirmed but still in progress
      affirmitiveAction = null;
    }
  } else if (g.status == AppointmentStatus.completed) {
    rejectStatus = null;
  }

  const confirmComponent = affirmitiveAction ? (
    <Button
      variant="outline"
      style={{ padding: "3px", fontSize: "14px" }}
      color="blue"
      onClick={() => affirmitiveAction?.()}
    >
      {affirmitiveText}
    </Button>
  ) : (
    <span style={{ color: "darkgray" }}>{affirmitiveText}</span>
  );

  const rejectButtonId = `reject-button-${g._id}`;
  const rejectComponent = rejectStatus && (
    <>
      <Button
        className={g.canReject ? "underlineOnHover" : ""}
        id={rejectButtonId}
        color="text"
        style={{
          padding: "3px",
          fontSize: "12px",
          marginTop: "10px",
          color: "darkgray",
          position: "absolute",
          right: "0",
          textAlign: "right",
        }}
        disabled={!g.canReject}
        onClick={() => beginReject(rejectStatus)}
      >
        <em>{rejectText}</em>
      </Button>
      {!g.canReject && (
        <Tooltip label="This appointment cannot be rejected">
          <span />
        </Tooltip>
      )}
    </>
  );

  const closeReject = () => beginReject(null);

  const time = `${toDateTime(g.start, props.venue?.tz).toFormat(
    "hh:mm a",
  )} to ${toDateTime(g.finish, props.venue?.tz).toFormat("hh:mm a")}`;
  return (
    <>
      <div style={{ display: "inline-block" }}>
        <div style={{ width: "65%", display: "inline" }}>
          <div style={{ position: "absolute", right: "20px" }}>
            {confirmComponent}
            <br />
            {!!rejectComponent && rejectComponent}
          </div>
          {g.warning && (
            <Alert color="yellow">
              <em>{g.warning}</em>
            </Alert>
          )}
          <p style={{ marginBottom: "6px" }}>
            <span style={{ fontSize: "16px", marginRight: "15px" }}>
              {time}
            </span>
          </p>
          <ShiftSessionLink
            shiftId={g.details.shiftId}
            sessionId={g.details.sessionId}
            col="darkgray"
          >
            {g.details.shiftName}
          </ShiftSessionLink>
          {/* <p style={{ fontSize: "12px", color: "darkgray", marginBottom: "4px" }}>Supervising: <em>{g.details.shiftName}</em></p> */}
          {/* <a style={{ fontSize: "12px", color: "darkgray", marginBottom: "4px" }}>Supervising: <em>{g.details.shiftName}</em></a> */}

          <p
            style={{
              marginBottom: "4px",
              marginTop: "8px",
              alignItems: "right",
            }}
          >
            {tags.map((t) => (
              <span
                style={{
                  padding: "5px",
                  marginRight: "5px",
                  borderRadius: "5px",
                  fontSize: "12px",
                  background: `${t.color}26`,
                  color: t.color,
                }}
                className="noselect"
                key={t.text}
              >
                {t.text.toLowerCase()}
              </span>
            ))}
          </p>
        </div>
        <div style={{ backgroundColor: "blue", width: "34%" }} />
      </div>
      <hr />
      {isRejectingStatus && (
        <Modal opened={Boolean(isRejectingStatus)} onClose={closeReject} title="Rejecting appointment">
          <RejectAppointment
            appointment={g._id}
            error={error}
            onClose={closeReject}
            onSave={() => props.didUpdateStatus(rejectStatus)}
          />
        </Modal>
      )}
    </>
  );
}

const mapState2Props = (state, props: IUpcomingSupervisorRowProps) => {
  if (!props.appointment) return;
  return {};
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ updateAppointmentStatus }, dispatch),
  };
}

export default connect(
  mapState2Props,
  mapDispatchToProps,
)(UpcomingSupervisorRow);
